/**
 * FirmSearchBox — autocomplete input that jumps users straight to a
 * firm's guide or reverse-search page. Reused by the navbar (compact
 * variant) and can drop into landing / uni-page CTAs.
 *
 * Fetches `/api/employers/curated` once and filters in-memory — the
 * catalogue is ~715 items so client-side search is fine and avoids
 * a round-trip on every keystroke.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X, ArrowRight, Briefcase } from "lucide-react";

export default function FirmSearchBox({
  variant = "default",           // "default" | "compact"
  placeholder,
  destination = "get-me-to",      // "get-me-to" | "how-to-get-into"
  className = "",
}) {
  const navigate = useNavigate();
  const [firms, setFirms] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(-1);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/employers/curated")
      .then((r) => { if (!cancelled) setFirms(r.data.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const matches = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn || !firms.length) return [];
    const sw = [], inc = [];
    for (const f of firms) {
      const n = (f.firm_name || "").toLowerCase();
      if (n.startsWith(qn)) sw.push(f);
      else if (n.includes(qn) || (f.slug || "").includes(qn)) inc.push(f);
    }
    return [...sw, ...inc].slice(0, 8);
  }, [q, firms]);

  useEffect(() => { setIdx(matches.length > 0 ? 0 : -1); }, [matches]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const jumpTo = (slug) => {
    const path = destination === "how-to-get-into"
      ? `/how-to-get-into/${slug}`
      : `/get-me-to/${slug}`;
    navigate(path);
    setOpen(false);
    setQ("");
  };

  const onKey = (e) => {
    if (!open || !matches.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % matches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + matches.length) % matches.length); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const pick = matches[idx] || matches[0];
      if (pick) jumpTo(pick.slug);
    }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  const compact = variant === "compact";
  const iconSize = compact ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <Search className={`${iconSize} text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none`} />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder || (compact ? "Get me to…" : "Search 715 firms — Goldman, McKinsey, Google, BlackRock…")}
        data-testid={`firm-search-input-${variant}`}
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-autocomplete="list"
        className={`w-full rounded-full border border-stone-300 bg-white font-underground ${compact ? "pl-8 pr-3 py-1.5 text-[13px]" : "pl-10 pr-9 py-2.5 text-sm"} outline-none focus:border-[#4C1D95]`}
      />
      {q && !compact && (
        <button
          type="button"
          onClick={() => { setQ(""); inputRef.current?.focus(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
          aria-label="Clear"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {open && matches.length > 0 && (
        <div
          data-testid={`firm-search-dropdown-${variant}`}
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-stone-200 bg-white shadow-lg overflow-hidden max-h-96 overflow-y-auto"
          role="listbox"
        >
          {matches.map((f, i) => (
            <Link
              key={f.slug}
              to={destination === "how-to-get-into" ? `/how-to-get-into/${f.slug}` : `/get-me-to/${f.slug}`}
              data-testid={`firm-search-option-${i}`}
              onMouseEnter={() => setIdx(i)}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 text-sm border-b border-stone-100 last:border-b-0 ${i === idx ? "bg-[#F5F0FF]" : "bg-white"}`}
            >
              <Briefcase className="h-3 w-3 text-[#4C1D95] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-stone-900 truncate">{f.firm_name}</div>
                <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                  {f.sector || "—"}
                </div>
              </div>
              <ArrowRight className="h-3 w-3 text-stone-400 shrink-0" />
            </Link>
          ))}
          <div className="px-3 py-1.5 bg-stone-50 text-[10px] font-underground-sc tracking-wider text-stone-500 border-t border-stone-100">
            ↑↓ move · Enter open · Esc close
          </div>
        </div>
      )}
    </div>
  );
}
